import { entre, Sacada, signo, VIDA_DEFECTOS, type Rng, type VidaOpts } from './vida';

/**
 * Capa de vida ambiental del avatar (`04_ARQUITECTURA.md` §6.6).
 *
 * La experiencia objetivo es una videollamada con una persona, no un
 * póster que cambia de cara por turnos. Entre turnos el avatar tiene que
 * seguir vivo, y eso es **100% responsabilidad del cliente**: el backend
 * no lo emite ni lo va a emitir.
 *
 * Cuatro canales, los cuatro baratos:
 *
 * 1. **Parpadeo** periódico con jitter.
 * 2. **Micro-desvíos de mirada** cada pocos segundos, como sacadas
 *    (tanda 8, `MiradaIdle`).
 * 3. **Oscilación leve de cabeza.**
 * 4. **Respiración** (tanda 4, D-3): un ciclo lento que el adapter
 *    traduce a movimiento del tronco, si el asset tiene ese hueso.
 *
 * Esta clase sólo produce desplazamientos; **no toca el rig**. Quien los
 * compone con la expresión dirigida es el `ThreeRigAdapter`. La
 * distinción importa: la última expresión emocional no debe apagar la
 * vida ambiental, así que se componen, no se reemplazan.
 *
 * `ExpressionEngine` y `avatar-core` no se enteran de que esto existe.
 */

/** Desplazamientos que la capa idle aporta en un instante dado. */
export interface IdleOffsets {
  /**
   * Cierre extra de párpados, `0..1`. Es relativo a la apertura que deje
   * la expresión: en 1 el ojo cierra del todo aunque la expresión lo
   * tuviera entreabierto, y nunca lo abre.
   */
  parpadeo: number;
  /** Desvío de mirada horizontal, se **suma** a `mirada_x`. */
  miradaX: number;
  /** Desvío de mirada vertical, se **suma** a `mirada_y`. */
  miradaY: number;
  /** Giro de cabeza en radianes, alrededor del eje vertical. */
  cabezaYaw: number;
  /** Cabeceo en radianes. */
  cabezaPitch: number;
  /**
   * Fase de la respiración, `0..1`: 0 al final de la espiración, 1 en la
   * inspiración máxima (tanda 4, D-3). Es una fase y no un ángulo porque
   * quién la traduce a movimiento es el adapter, según qué huesos tenga
   * el asset.
   */
  respiracion: number;
}

export const IDLE_NEUTRO: IdleOffsets = {
  parpadeo: 0,
  miradaX: 0,
  miradaY: 0,
  cabezaYaw: 0,
  cabezaPitch: 0,
  respiracion: 0,
};

/** Duración de un parpadeo completo, cierre + apertura. */
export const PARPADEO_MS = 140;
/** El cierre es más rápido que la apertura: así parpadea un humano. */
const PARPADEO_FRACCION_CIERRE = 0.35;
/** Rango del intervalo entre parpadeos. El jitter evita el metrónomo. */
const PARPADEO_INTERVALO_MIN_MS = 2600;
const PARPADEO_INTERVALO_MAX_MS = 6200;

/** Amplitud de la oscilación de cabeza. Muy poco: 1.2° y 0.7°. */
const CABEZA_YAW_MAX_RAD = (1.2 * Math.PI) / 180;
const CABEZA_PITCH_MAX_RAD = (0.7 * Math.PI) / 180;
/**
 * Períodos deliberadamente inconmensurables. Con períodos que encajan,
 * el patrón se repite y el ojo lo detecta como mecánico.
 */
const CABEZA_YAW_PERIODO_MS = 7300;
const CABEZA_PITCH_PERIODO_MS = 11700;

/**
 * Ciclo respiratorio completo, inspiración + espiración. 4,2 s son unas
 * 14 respiraciones por minuto: el reposo de un adulto. Es primo con los
 * períodos de la cabeza, para que el conjunto no caiga en un patrón.
 */
const RESPIRACION_PERIODO_MS = 4200;

export type { Rng };

/**
 * Forma de un parpadeo según su progreso `0..1`: cierre rápido y apertura
 * más lenta. La comparten la idle y el parpadeo en pausa de los gestos.
 */
export function curvaParpadeo(fase: number): number {
  return fase < PARPADEO_FRACCION_CIERRE
    ? fase / PARPADEO_FRACCION_CIERRE
    : 1 - (fase - PARPADEO_FRACCION_CIERRE) / (1 - PARPADEO_FRACCION_CIERRE);
}

/**
 * Micro-desvíos de mirada de la idle (tanda 8, pieza 1).
 *
 * Cada 2–5 s, con jitter, elige un objetivo: la mayoría de las veces el
 * centro —la cámara, el alumno— y si no un punto chico fuera de él. Va
 * hasta ahí con una **sacada** de 50–80 ms y lo sostiene hasta el próximo
 * salto. Así se mira a alguien en una videollamada: casi siempre a los
 * ojos, con escapadas breves.
 *
 * Hasta la tanda 8 el canal existía pero no se percibía: elegía un punto
 * uniforme en ±0,09 —en promedio menos de 1° de giro— y llegaba con un
 * suavizado exponencial de 260 ms que tardaba cerca de un segundo en
 * asentarse. Nunca volvía al centro, así que tampoco había contraste.
 */
export class MiradaIdle {
  private readonly sacada = new Sacada();
  private msHastaSalto: number;
  /** Cuántos saltos lleva, y cuántos fueron al centro. Para los tests. */
  saltos = 0;
  saltosAlCentro = 0;

  constructor(
    private readonly rng: Rng,
    private vida: Readonly<VidaOpts> = VIDA_DEFECTOS,
  ) {
    this.msHastaSalto = this.intervalo();
  }

  configurar(vida: Readonly<VidaOpts>): void {
    this.vida = vida;
  }

  /** Próximo intervalo entre saltos, con jitter. */
  intervalo(): number {
    return entre(this.rng, this.vida.miradaIdleIntervaloMinMs, this.vida.miradaIdleIntervaloMaxMs);
  }

  /** Próximo objetivo: el centro, o un desvío dentro de las amplitudes. */
  objetivo(): { x: number; y: number } {
    const v = this.vida;
    if (this.rng() < v.miradaIdleProbCentro) return { x: 0, y: 0 };
    return {
      x: signo(this.rng) * entre(this.rng, v.miradaIdleXMin, v.miradaIdleXMax),
      y: signo(this.rng) * entre(this.rng, v.miradaIdleYMin, v.miradaIdleYMax),
    };
  }

  get destinoX(): number {
    return this.sacada.destinoX;
  }

  get destinoY(): number {
    return this.sacada.destinoY;
  }

  update(deltaMs: number): Sacada {
    this.msHastaSalto -= deltaMs;
    if (this.msHastaSalto <= 0) {
      const { x, y } = this.objetivo();
      this.saltos++;
      if (x === 0 && y === 0) this.saltosAlCentro++;
      this.sacada.ir(x, y, entre(this.rng, this.vida.sacadaMinMs, this.vida.sacadaMaxMs));
      this.msHastaSalto = this.intervalo();
    }
    this.sacada.update(deltaMs);
    return this.sacada;
  }
}

export interface IdleLayerOpts {
  /** Default `Math.random`. Los tests pasan una secuencia determinista. */
  rng?: Rng;
  /** Constantes de la mirada. Default `VIDA_DEFECTOS`. */
  vida?: Readonly<VidaOpts>;
  /** Si los micro-desvíos de mirada están encendidos. Default `true`. */
  miradaActiva?: boolean;
}

/**
 * Genera la vida ambiental. Se tickea desde el mismo render loop que el
 * resto del adapter.
 */
export class IdleLayer {
  private readonly rng: Rng;

  /** Tiempo acumulado, para las oscilaciones de cabeza. */
  private tMs = 0;

  private msHastaParpadeo: number;
  /** Progreso del parpadeo en curso, `0..1`. `null` = no hay parpadeo. */
  private faseParpadeo: number | null = null;
  /**
   * Multiplica el avance hacia el próximo parpadeo. Mientras el paciente
   * habla baja: el parpadeo pasa a caer en las pausas (pieza 2).
   */
  private tasaParpadeo = 1;

  private readonly mirada: MiradaIdle;
  private miradaActiva: boolean;

  constructor(opts: IdleLayerOpts = {}) {
    this.rng = opts.rng ?? Math.random;
    this.msHastaParpadeo = this.intervaloParpadeo();
    this.mirada = new MiradaIdle(this.rng, opts.vida);
    this.miradaActiva = opts.miradaActiva ?? true;
  }

  configurar(vida: Readonly<VidaOpts>): void {
    this.mirada.configurar(vida);
  }

  /** Apagar la mirada la deja al frente; el resto de la idle sigue. */
  setMiradaActiva(activa: boolean): void {
    this.miradaActiva = activa;
  }

  /** `1` es la tasa basal; menos, parpadea menos seguido. */
  setTasaParpadeo(tasa: number): void {
    this.tasaParpadeo = Math.max(0, tasa);
  }

  private intervaloParpadeo(): number {
    return entre(this.rng, PARPADEO_INTERVALO_MIN_MS, PARPADEO_INTERVALO_MAX_MS);
  }

  /** Avanza la capa y devuelve los desplazamientos de este frame. */
  update(deltaMs: number): IdleOffsets {
    this.tMs += deltaMs;

    return {
      parpadeo: this.avanzarParpadeo(deltaMs),
      ...this.avanzarMirada(deltaMs),
      cabezaYaw: Math.sin((this.tMs / CABEZA_YAW_PERIODO_MS) * Math.PI * 2) * CABEZA_YAW_MAX_RAD,
      cabezaPitch:
        Math.sin((this.tMs / CABEZA_PITCH_PERIODO_MS) * Math.PI * 2) * CABEZA_PITCH_MAX_RAD,
      // Seno desplazado a `0..1`: la inspiración y la espiración duran lo
      // mismo. Una respiración real tiene la espiración más larga, pero a
      // esta amplitud la diferencia no se ve (P-2).
      respiracion: (1 - Math.cos((this.tMs / RESPIRACION_PERIODO_MS) * Math.PI * 2)) / 2,
    };
  }

  private avanzarParpadeo(deltaMs: number): number {
    if (this.faseParpadeo === null) {
      this.msHastaParpadeo -= deltaMs * this.tasaParpadeo;
      if (this.msHastaParpadeo > 0) return 0;
      this.faseParpadeo = 0;
      this.msHastaParpadeo = this.intervaloParpadeo();
    }

    this.faseParpadeo += deltaMs / PARPADEO_MS;
    if (this.faseParpadeo >= 1) {
      this.faseParpadeo = null;
      return 0;
    }
    return curvaParpadeo(this.faseParpadeo);
  }

  private avanzarMirada(deltaMs: number): { miradaX: number; miradaY: number } {
    // La mirada se sigue tickeando apagada, así al encenderla retoma su
    // ritmo en vez de saltar enseguida; sólo deja de aportar.
    const m = this.mirada.update(deltaMs);
    return this.miradaActiva ? { miradaX: m.x, miradaY: m.y } : { miradaX: 0, miradaY: 0 };
  }
}
